import type { Product } from 'services/generated/commerce-service/models/Product';
import type { UpgradeMethod } from 'services/generated/commerce-service/models/UpgradeMethod';
import type { Variant } from 'services/generated/commerce-service/models/Variant';
import { getFirstVariant } from 'ui/utils/productHelpers';
import { getPriceValues } from 'ui/utils/variantHelpers';
import type { DevicesAndTiersResponse } from 'services/generated/commerce-service/models/DevicesAndTiersResponse';
import type { UserCheckoutDetails } from 'ui/types/cartTypes';
import { BLOCKS } from '@contentful/rich-text-types';
import type { UserCartInfoResponse } from 'services/generated/commerce-service/models/UserCartInfoResponse';
import type { Attribute } from 'services/generated/commerce-service/models/Attribute';
import type { Region } from '@whoop/i18n/types/internationalization';
import { UpgradeIneligibleErrorType } from '../constants/upgrades';
import {
  CONTENTFUL_DEFAULT,
  JOIN_FLOW_STEPS,
  LIFE_PERKS,
  LIFE_PERKS_NO_ECG,
  MembershipIdentifyingAttributes,
  MembershipTier,
  ONE_PERKS,
  PEAK_PERKS,
  UPGRADE_STEPS,
  VARIANT_ATTRIBUTE_NAMES,
  ONE_UPGRADE_PERKS,
  PEAK_UPGRADE_PERKS_ONE_PRESENT,
  PEAK_UPGRADE_PERKS_ONE_NOT_PRESENT,
  MG_DEFAULT_BAND_KEY,
  PEAK_DEFAULT_BAND_KEY,
  ONE_DEFAULT_BAND_KEY,
  TRIAL_DEFAULT_BAND_KEY,
  TRIAL,
  FEATURE_AVAILABILITY_FEATURE_NAMES,
  CONTENTFUL_JOIN_FLOW_TYPE,
  CONTENTFUL_UPGRADE_FLOW_TYPE,
} from '@/lib/constants/maverick';
import type {
  BillingType,
  ContentfulDisclaimer,
  DefaultDisclaimer,
  Disclaimer,
  MembershipTierCardImage,
  MembershipTierComparisonFeature,
  MembershipTierComparisonFeatureList,
  MembershipTierWithTrial,
  RegionalDisclaimer,
} from '@/lib/constants/maverick';
import type {
  MembershipTierInfo,
  WhatsIncludedTierInfo,
} from '@/types/maverickTypes';
import type { DeviceHeaderDetails, UpgradeOption } from '@/types/upgradeTypes';
import { FeatureAvailability } from '@/hooks/featureAvailabilityClass';

export const isValidTier = (tier: string): boolean => {
  return Object.values(MembershipTier).includes(tier as MembershipTier);
};

export const isValidTierIncludingTrial = (tier: string): boolean => {
  return (
    Object.values(MembershipTier).includes(tier as MembershipTier) ||
    tier === TRIAL
  );
};
interface GetTierProps {
  tier: MembershipTier;
  currentTier?: MembershipTier;
  featureAvailability: FeatureAvailability | undefined;
  region: Region;
}
/**
 * Returns the perks associated with a given membership tier
 * Shared across upgrade membership cards + device header and join flow membership cards
 *
 * If `currentTier` is defined, the user is viewing these cards from the upgrade flow.
 * In the upgrade flow, the PEAK content changes based on whether the user's current tier is ONE or PEAK.
 * -- If the user's current tier is ONE, the user will see PEAK content that includes mention of ONE
 * -- If the user's current tier is PEAK, the user will not see any content related to ONE
 */
export const getTierPerks = ({
  tier,
  currentTier,
  featureAvailability,
  region,
}: GetTierProps) => {
  switch (tier) {
    case MembershipTier.ONE:
      if (currentTier === MembershipTier.ONE) {
        return ONE_UPGRADE_PERKS;
      }
      return ONE_PERKS;
    case MembershipTier.PEAK:
      if (currentTier === MembershipTier.ONE) {
        return PEAK_UPGRADE_PERKS_ONE_PRESENT;
      } else if (currentTier === MembershipTier.PEAK) {
        return PEAK_UPGRADE_PERKS_ONE_NOT_PRESENT;
      }
      return PEAK_PERKS;
    case MembershipTier.LIFE:
      if (!featureAvailability) {
        return LIFE_PERKS;
      }

      const ecg = featureAvailability.getFeatureStatus(
        FEATURE_AVAILABILITY_FEATURE_NAMES.ECG,
        region,
      );
      const ihrn = featureAvailability.getFeatureStatus(
        FEATURE_AVAILABILITY_FEATURE_NAMES.IHRN,
        region,
      );

      const ecgIsAvailableIHRNIsUnavailable =
        ecg.isAvailable && (ihrn.isUnavailable || ihrn.isComingSoon);

      // this solution works because we are not mutating the original perks array
      // for some reason spreading the perks does not work, and of course neither does returning either of the perks' references from the ternary
      const perks = JSON.parse(
        JSON.stringify(
          ecgIsAvailableIHRNIsUnavailable ? LIFE_PERKS_NO_ECG : LIFE_PERKS,
        ),
      );

      if (ecg.isUnavailable) {
        perks[perks.length - 2].iconStyle = 'info';
      }

      if (ihrn.isUnavailable && ecg.isUnavailable) {
        perks[perks.length - 1].iconStyle = 'info';
      }

      return perks;
    default:
      return [];
  }
};

//JF specific function to format BE response to device-tiers endpoint
export function parseAvailableTiers(
  memberships: Product[],
  membershipTierCardImages?: MembershipTierCardImage[],
): MembershipTierInfo[] {
  const availableTiers: MembershipTierInfo[] = [];

  const addTier = (tier: MembershipTier) => {
    // Filter out family and trial memberships
    const tierMemberships = getTierCompatibleProducts(memberships, tier);
    const membershipProduct = tierMemberships
      ? getMembershipByAttributes({
          memberships: tierMemberships,
          trialDays: 365,
          isFamily: false,
        })
      : undefined;
    if (membershipProduct) {
      availableTiers.push({
        name: tier,
        membershipProduct,
      });
    }
  };

  addTier(MembershipTier.ONE);
  addTier(MembershipTier.PEAK);
  addTier(MembershipTier.LIFE);

  // add image for membership card from contentful to tier if exists
  const availableTiersWithCardImages = availableTiers.map((tier) => {
    const matchingImage = membershipTierCardImages?.find(
      (img) => img.tier.tierName === tier.name,
    );

    return {
      ...tier,
      membershipTierCardImage: matchingImage,
    };
  });

  return availableTiersWithCardImages;
}

// Returns true if membership variant is in the provided tier
export function isVariantTierCompatible(
  variant: Variant,
  tier: MembershipTier,
): boolean {
  return variant.attributes.some(
    (attr) =>
      attr.name === VARIANT_ATTRIBUTE_NAMES.TIER && attr.value.key === tier,
  );
}

// Returns a membership product that matches the provided trial days and family size if provided
export function getMembershipByAttributes({
  memberships,
  trialDays,
  isFamily,
}: {
  memberships: Product[];
  trialDays: number;
  isFamily?: boolean;
}): Product | undefined {
  const membership = memberships.find((membership) => {
    return membership?.variants.some((variant) => {
      let trialDaysMatch = false;
      let hasFamilySize = false;
      variant.attributes.forEach((attr) => {
        if (
          attr.name === VARIANT_ATTRIBUTE_NAMES.TRIAL_DAYS &&
          attr.value === trialDays
        ) {
          trialDaysMatch = true;
        }
        if (
          attr.name === VARIANT_ATTRIBUTE_NAMES.FAMILY_SIZE &&
          Boolean(attr.value)
        ) {
          hasFamilySize = true;
        }
      });
      return isFamily
        ? trialDaysMatch && hasFamilySize
        : trialDaysMatch && !hasFamilySize;
    });
  });
  return membership;
}

// Returns the family size of the first variant of a family membership
// this is useful to check if a membership is a family membership in the join flow
// or to get the family size associated with a family admin's membership in the upgrade flow
export const getProductFamilySizeByAttributes = (
  membershipProduct: Product,
): number | undefined => {
  let familySize;
  membershipProduct.variants.forEach((variant: Variant) =>
    variant.attributes.some((attr) => {
      if (attr.name === VARIANT_ATTRIBUTE_NAMES.FAMILY_SIZE) {
        familySize = attr.value;
      }
    }),
  );
  return familySize;
};

// Used in Upgrade flow to get the family variant by family size
export const getFamilyVariantBySize = ({
  membershipProduct,
  familySize,
}: {
  membershipProduct: Product;
  familySize: number;
}): Variant | undefined => {
  return membershipProduct.variants.find((variant) => {
    return variant.attributes.some(
      (attr) =>
        attr.name === VARIANT_ATTRIBUTE_NAMES.FAMILY_SIZE &&
        attr.value === familySize,
    );
  });
};

// Server side Upgrade flow specific method that takes a membership product returns
// a product with just the variants that matches the family admin's family size.
// This allows us to pass only the relevant family variants to the client and for
// client to know the admin's family size.
export const getFamilyProductWithFilteredVariants = ({
  familySize,
  membershipProduct,
}: {
  familySize: number;
  membershipProduct: Product;
}): Product | undefined => {
  const familyVariant = getFamilyVariantBySize({
    membershipProduct,
    familySize,
  });
  if (familyVariant) {
    // remove other variants from family membership product
    return { ...membershipProduct, variants: [familyVariant] };
  }
};

// Upgrade specific function to format BE response to device-tiers endpoint (devices returned matter)
export function parseAvailableDevicesTiers(
  userDevicesTiers: DevicesAndTiersResponse,
  membershipTierCardImages?: MembershipTierCardImage[],
): MembershipTierInfo[] {
  const { devices, memberships, current_tier, family_size } = userDevicesTiers;
  if (!devices || !memberships) return [];
  const availableTiers: MembershipTierInfo[] = [];
  Object.values(MembershipTier).forEach((desiredTier) => {
    let membershipProduct = getTierCompatibleProducts(
      memberships,
      desiredTier,
    )?.[0];

    // family_size will only exist if a family admin is viewing the upgrade flow
    if (family_size && membershipProduct) {
      membershipProduct = getFamilyProductWithFilteredVariants({
        familySize: family_size,
        membershipProduct,
      });
    }

    // If both membership and device products are found, add to available tiers
    const deviceProduct = getTierCompatibleProducts(devices, desiredTier)?.[0];
    if (membershipProduct && deviceProduct) {
      availableTiers.push({
        name: desiredTier,
        membershipProduct,
        usersCurrentTier: current_tier as MembershipTier,
      });
    }
  });

  // add image for membership card from contentful to tier if exists
  const availableTiersWithCardImages = availableTiers.map((tier) => {
    const matchingImage = membershipTierCardImages?.find(
      (img) => img.tier.tierName === tier.name,
    );

    return {
      ...tier,
      membershipTierCardImage: matchingImage,
    };
  });

  return availableTiersWithCardImages;
}

// Given a list of products, return list of products compatible with provided tier
// Compatibility determined by membership-tier-type attribute & device-type attribute on CT product
export function getTierCompatibleProducts(
  products: Product[],
  tier: MembershipTier,
): Product[] | undefined {
  const membershipsInTier = products.filter((membership) => {
    return membership.variants.some((variant) => {
      return isVariantTierCompatible(variant, tier);
    });
  });
  return membershipsInTier;
}

//Given a list of memberships, return the prices for the membership that is compatible with the provided tier
export function getMembershipPriceFromTier({
  memberships,
  tier,
  familySize,
}: {
  memberships: Product[];
  tier: MembershipTier;
  familySize?: number;
}): number | undefined {
  const chosenProduct = getTierCompatibleProducts(memberships, tier)?.[0];
  if (!chosenProduct) return;
  let chosenVariant;
  if (familySize) {
    chosenVariant = getFamilyVariantBySize({
      membershipProduct: chosenProduct,
      familySize,
    });
  } else {
    chosenVariant = getFirstVariant(chosenProduct);
  }
  return getPriceValues(chosenVariant)?.displayPrice ?? 0;
}

// Filters device header options based on compatibility with users devices and tiers
export function filterDeviceHeaderOptions(
  deviceHeaderContent: DeviceHeaderDetails[],
  deviceTiersResponse: DevicesAndTiersResponse,
): DeviceHeaderDetails[] {
  const { devices } = deviceTiersResponse;
  return deviceHeaderContent.filter((headerItem) => {
    return Boolean(
      getTierCompatibleProducts(devices, headerItem.compatibleTier)?.[0],
    );
  });
}

//Function to format BE response to FE option objects
export const parseUpgradeOptions = (
  upgradeMethodResponse: UpgradeMethod[],
): UpgradeOption[] => {
  return upgradeMethodResponse.map(
    ({
      product,
      base_price,
      proration,
      exchange,
      renewal,
      renews_at,
      months_until_renewal,
      family_size,
    }) => {
      const filteredProduct = family_size
        ? getFamilyProductWithFilteredVariants({
            membershipProduct: product,
            familySize: family_size,
          })
        : product;

      return {
        product: filteredProduct || product,
        basePrice: base_price,
        proration: proration ?? undefined,
        exchange: exchange ?? undefined,
        renewsAt: renewal?.renews_at ?? renews_at,
        monthsUntilRenewal:
          renewal?.months_until_renewal ?? months_until_renewal,
      };
    },
  );
};

const replaceNullValuesWithUndefined = (obj: any) => {
  return JSON.parse(JSON.stringify(obj), (key, value) => {
    return value === null ? undefined : value;
  });
};

export const parseUserCartInfo = (
  userCartInfoResponse: UserCartInfoResponse,
): UserCheckoutDetails => {
  const userInfo: UserCheckoutDetails = {
    savedAddress: userCartInfoResponse.saved_address
      ? replaceNullValuesWithUndefined(userCartInfoResponse.saved_address)
      : null,
    paymentDescription: null,
    paymentId: null,
  };
  const savedPayment = userCartInfoResponse.saved_payment;
  if (savedPayment) {
    userInfo.paymentDescription = savedPayment.saved_payment_description;
    userInfo.paymentId = savedPayment.saved_payment_method_id;
  }
  return userInfo;
};

export const isKnownIneligibleErrorType = (errorType: string): boolean => {
  return Object.values(UpgradeIneligibleErrorType).includes(
    errorType as UpgradeIneligibleErrorType,
  );
};

// todo: isRegionalDisclaimer and isDefaultDisclaimer (below) check whether properties exist, not their values (regionalContent, defaultContent, order)
// post-launch, we should check values (disclqaimer.regionalContent !== null, disclaimer.defaultContent !== null, and disclaimer.order !== null)
// expect these changes to have downstream effects and adjust accordingly

/**
 * Returns true if the given disclaimer from contentful is a correctly configured regional disclaimer
 */
// todo: see note above about isRegionalDisclaimer
export const isRegionalDisclaimer = (
  disclaimer: ContentfulDisclaimer,
): boolean => {
  return (
    !disclaimer.region.includes(CONTENTFUL_DEFAULT) &&
    'regionalContent' in disclaimer
  );
};

/**
 * Returns true if the given disclaimer from contentful is a correctly configured default disclaimer
 */
// todo: see note above about isDefaultDisclaimer
export const isDefaultDisclaimer = (
  disclaimer: ContentfulDisclaimer,
): boolean => {
  return (
    disclaimer.region.includes('default') &&
    'globalContent' in disclaimer &&
    'order' in disclaimer
  );
};

/**
 * Organizes disclaimers fetched from contentful to be in sorted order by 'order' property,
 * with regional content (ex: US-specific ECG messaging) added for the current region if it exists.
 */
export const getDisclaimersToDisplay = ({
  disclaimers,
  tier,
  isUpgrade,
}: {
  disclaimers: ContentfulDisclaimer[];
  tier?: MembershipTierWithTrial;
  isUpgrade?: boolean;
}): Disclaimer[] => {
  const groupedDisclaimersById: ContentfulDisclaimer[][] = [];
  const disclaimerMap: Record<string, ContentfulDisclaimer[]> = {};
  const flowType = isUpgrade
    ? CONTENTFUL_UPGRADE_FLOW_TYPE
    : CONTENTFUL_JOIN_FLOW_TYPE;

  for (const disclaimer of disclaimers) {
    // filter by tier - default to true if no tier prop was passed in
    let isTierCompatible = true;
    if (tier) {
      // will flip to false if tier prop was passed in with no matches in compatibleTiers
      isTierCompatible = disclaimer.compatibleTiersCollection.items.some(
        (membershipTier) =>
          membershipTier.tierName && membershipTier.tierName === tier,
      );
    }

    // filter by flowType - CONTENTFUL_DEFAULT represents a disclaimer for both flows
    const flowTypeMatch = [flowType, CONTENTFUL_DEFAULT].includes(
      disclaimer.flow?.flowType,
    );

    // add disclaimer to map
    if (isTierCompatible && flowTypeMatch) {
      if (!disclaimerMap[disclaimer.id]) {
        disclaimerMap[disclaimer.id] = [];
      }
      disclaimerMap[disclaimer.id].push(disclaimer);
    }
  }

  // group disclaimers by id
  for (const key in disclaimerMap) {
    groupedDisclaimersById.push(disclaimerMap[key]);
  }

  let disclaimersToDisplay: Disclaimer[] = [];
  Object.values(groupedDisclaimersById).forEach((disclaimerGroup) => {
    if (!disclaimerGroup) return;

    // find the first default disclaimer
    const defaultDisclaimer = disclaimerGroup.find(
      isDefaultDisclaimer,
    ) as DefaultDisclaimer;

    // find the first regional disclaimer
    const regionalDisclaimer = disclaimerGroup.find(
      isRegionalDisclaimer,
    ) as RegionalDisclaimer;

    if (!defaultDisclaimer) return;

    // if there is regional content, display it (importantly isRegionalDisclaimer checks that the regionalDisclaimer does not have 'default' for region)
    if (regionalDisclaimer) {
      disclaimersToDisplay.push({
        // we are spreading in defaultDisclaimer data here to preserve order
        // order does not exist on regionalDisclaimer (see type)
        ...defaultDisclaimer,
        regionalContent: regionalDisclaimer.regionalContent,
        compareTableContent: regionalDisclaimer.compareTableContent ?? {
          json: { nodeType: BLOCKS.DOCUMENT, content: [], data: {} },
        },
      });
    }
    // else display the default disclaimer, but include some fallbacks so any potentially misconfigured contentful data does not break the site
    else {
      disclaimersToDisplay.push({
        ...defaultDisclaimer,
        // we only want to display one footer per feature, so we'll use the first
        // you can add globalContent (footer disclaimer) to all default disclaimers in Contentful to be sure one of them gets displayed (this will be refactored post-launch)
        globalContent: defaultDisclaimer.globalContent ?? {
          json: { nodeType: BLOCKS.DOCUMENT, content: [], data: {} },
        },
        regionalContent: defaultDisclaimer.regionalContent ?? {
          json: { nodeType: BLOCKS.DOCUMENT, content: [], data: {} },
        },
      });
    }
  });

  // return disclaimers sorted by 'order'
  return disclaimersToDisplay.sort((a, b) => a.order - b.order);
};

export function getSortedWhatsIncludedContent(
  whatsIncludedContent: WhatsIncludedTierInfo[],
  availableTierSimple: MembershipTierWithTrial[],
): WhatsIncludedTierInfo[] {
  return whatsIncludedContent.sort((a, b) => {
    const indexA = availableTierSimple.indexOf(a.tier);
    const indexB = availableTierSimple.indexOf(b.tier);

    // Puts the Trial content at the end.
    // Index is -1 for trials because trial is not a tier
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
}

export function getAllMembershipVariants(memberships: Product[]): {
  annualMembership?: Variant;
  twoYearMembership?: Variant;
  trialMembership?: Variant;
  familyMemberships?: Variant[];
} {
  // get trial membership if it exists
  const trialMembership = getMembershipByAttributes({
    memberships,
    ...MembershipIdentifyingAttributes.trial,
  })?.variants[0];

  // get annual, two year, and family membership variants from memberships
  const annualMembership = getMembershipByAttributes({
    memberships,
    ...MembershipIdentifyingAttributes.annual,
  })?.variants[0];
  const twoYearMembership = getMembershipByAttributes({
    memberships,
    ...MembershipIdentifyingAttributes.twoYear,
  })?.variants[0];
  const familyMemberships = getMembershipByAttributes({
    memberships,
    ...MembershipIdentifyingAttributes.family,
  })?.variants;
  return {
    annualMembership,
    twoYearMembership,
    trialMembership,
    familyMemberships,
  };
}

export function isJFStepsPage(currentPage: JOIN_FLOW_STEPS | UPGRADE_STEPS) {
  return [
    JOIN_FLOW_STEPS.JOIN,
    JOIN_FLOW_STEPS.BANDS,
    JOIN_FLOW_STEPS.MEMBERSHIP,
    JOIN_FLOW_STEPS.ACCESSORIES,
    JOIN_FLOW_STEPS.FAMILY,
  ].includes(currentPage as JOIN_FLOW_STEPS);
}

export function isUpgradeStepsPage(
  currentPage: JOIN_FLOW_STEPS | UPGRADE_STEPS,
) {
  return [
    UPGRADE_STEPS.UPGRADE,
    UPGRADE_STEPS.OPTIONS,
    UPGRADE_STEPS.UPCYCLE,
    UPGRADE_STEPS.ACCESSORIES,
  ].includes(currentPage as UPGRADE_STEPS);
}

export function getPeakTrialProduct(
  memberships: Product[],
): Product | undefined {
  const peakMemberships =
    getTierCompatibleProducts(memberships, MembershipTier.PEAK) || [];

  return getMembershipByAttributes({
    memberships: peakMemberships,
    ...MembershipIdentifyingAttributes.trial,
  });
}

export const getTierFromProduct = (product: Product) => {
  const variant = getFirstVariant(product);
  return getTierFromAttributes(variant.attributes);
};

export const getTierFromAttributes = (attributes: Attribute[]) => {
  const tierAttribute = attributes.find(
    (attr) => attr.name === VARIANT_ATTRIBUTE_NAMES.TIER,
  );
  return tierAttribute?.value.key as MembershipTier;
};

export const getBillingTypeFromProduct = (
  product: Product,
): BillingType | undefined => {
  const variant = getFirstVariant(product);
  const attribute = variant?.attributes.find(
    (attr) => attr.name === VARIANT_ATTRIBUTE_NAMES.BILLING_TYPE,
  );
  if (!attribute) return;
  return attribute.value.key as BillingType;
};

export const moveToComparisonTable = () => {
  const element = document.getElementById('membership-comparison-table');
  if (element) {
    const yOffset = -64; // Move up by 64 pixels so that compare table header visible below page headers
    const yPosition =
      element.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: yPosition, behavior: 'smooth' });
  }
};

export const findDefaultBand = (
  products: Product[],
  tier: MembershipTierWithTrial,
) => {
  let defaultKey;
  if (tier === MembershipTier.LIFE) {
    defaultKey = MG_DEFAULT_BAND_KEY;
  } else if (tier === MembershipTier.ONE) {
    defaultKey = ONE_DEFAULT_BAND_KEY;
  } else if (tier === TRIAL) {
    defaultKey = TRIAL_DEFAULT_BAND_KEY;
  } else {
    defaultKey = PEAK_DEFAULT_BAND_KEY;
  }
  return products.find((p) => p.key === defaultKey);
};

// Sanitizes compare table feature lists returned by contentful
// to use features for the current region if they exist, else 'default' region features
export const filterFeatureListsByRegion = (
  lists: MembershipTierComparisonFeatureList[],
  currentRegion: Region,
): MembershipTierComparisonFeatureList[] => {
  return lists.map((list) => {
    const groupedFeaturesById: Record<
      string,
      MembershipTierComparisonFeature[]
    > = {};
    // Group features by ID
    for (const feature of list.featuresCollection.items) {
      if (!groupedFeaturesById[feature.id]) {
        groupedFeaturesById[feature.id] = [];
      }
      groupedFeaturesById[feature.id].push(feature);
    }

    // If there's a region-specific version of the feature, use that one
    // Otherwise use default feature if there is one
    const resolvedFeatures = Object.values(groupedFeaturesById)
      .map((featureGroup) => {
        const regionSpecific = featureGroup.find(
          (feature) => feature.region?.includes(currentRegion),
        );
        const defaultFallback = featureGroup.find(
          (feature) => feature.region?.includes(CONTENTFUL_DEFAULT),
        );
        return regionSpecific || defaultFallback;
      })
      .filter((feature): feature is MembershipTierComparisonFeature =>
        Boolean(feature),
      ); // filter out undefined in case regions misconfigured on contentful and default doesnt exist

    const newList = {
      ...list,
      featuresCollection: { items: resolvedFeatures },
    };
    return newList;
  });
};
